import type { Immutable } from "immer";
import React from "react";
import { t } from "ttag";

import { templates } from "@reactivated";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import styles from "./index.module.scss";

type IEntry = templates.SiteMapDocumentTemplate["entries"][number];

type ITreeNode = {
    title: string;
    url: string;
    children: ITreeNode[];
};

// Rebuild the flat, level-tagged page list (treebeard annotated order) into a
// nested tree so React can emit balanced <ul>/<li> markup. A recursive
// *component* is fine here; only recursive reactivated *types* are avoided.
const buildTree = (entries: readonly IEntry[]): ITreeNode[] => {
    const roots: ITreeNode[] = [];
    const stack: ITreeNode[] = [];
    for (const entry of entries) {
        const node: ITreeNode = {
            title: entry.title,
            url: entry.url,
            children: [],
        };
        // `level` is 0-based (treebeard depth-minus-start_depth); trim the
        // stack back to this node's parent depth before re-pushing.
        stack.length = entry.level;
        const parent = stack[stack.length - 1];
        if (parent) {
            parent.children.push(node);
        } else {
            roots.push(node);
        }
        stack.push(node);
    }
    return roots;
};

const SiteMapNodes = (
    props: Immutable<{ nodes: readonly ITreeNode[] }>,
): React.JSX.Element => (
    <ul>
        {props.nodes.map((node) => (
            <li key={node.url}>
                <a href={node.url}>{node.title}</a>
                {node.children.length > 0 && (
                    <SiteMapNodes nodes={node.children} />
                )}
            </li>
        ))}
    </ul>
);

export const SiteMapBody = (
    props: Immutable<{ entries: templates.SiteMapDocumentTemplate["entries"] }>,
): React.JSX.Element => {
    const tree = buildTree(props.entries);
    return (
        <div className={concatClassNames([styles.sitemap, "l-capped-width"])}>
            <div>
                <h2>{t`Sitemap`}</h2>
            </div>
            <div className={styles.sitemapLinks}>
                <SiteMapNodes nodes={tree} />
            </div>
        </div>
    );
};
